import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { HiArrowRight } from 'react-icons/hi';
import data from '../../data/index.json';
import { Section, SectionHeader, reveal } from '../../components';
import communityImage from '../../assets/innovation-week.jpg';

const impact = [
  // Three house brands carry the merch line: Hyphen Tech, AgriHyphen AI, COSEBU.
  { value: '3', label: 'Brands Worn' },
  { value: '10+', label: 'Countries' },
  { value: '100%', label: 'Satisfaction' },
  { value: '24/7', label: 'Global Store' },
];

const Community = () => {
  const { t } = useTranslation();
  const supporters = data.communitySupporters || [];

  return (
    <Section id="community" tone="warm">
      <SectionHeader
        index="11"
        label={t('Global Ambassadors')}
        title={
          <>
            {t('Built by')} <em>{t('community')}</em>
          </>
        }
        lede={t(
          'Technical excellence meets cultural identity. Our community of developers, innovators, and fans represents the bold new era of African tech.'
        )}
        aside={
          <Link to="/store" className="btn btn-solid">
            {t('Explore Boutique')}
            <HiArrowRight aria-hidden />
          </Link>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-14 md:mt-20 items-start">

        {/* The community, wearing it. */}
        <motion.figure {...reveal()} className="lg:col-span-6 group">
          <div className="overflow-hidden bg-paper-tint aspect-[4/5] lg:aspect-[4/5]">
            <img
              src={communityImage}
              alt={t('The AgriHyphen AI team at Innovation Week in Burundi, wearing Hyphen branded shirts')}
              loading="lazy"
              className="img-editorial"
            />
          </div>
          <figcaption className="flex justify-between gap-4">
            <span>{t('AgriHyphen AI at Innovation Week, Bujumbura')}</span>
            <span className="tabular shrink-0">UNDP</span>
          </figcaption>
        </motion.figure>

        {/* Voices — set as pull quotes rather than hover-reveal photo cards. */}
        <div className="lg:col-span-6 lg:pt-4">
          {supporters.map((supporter, idx) => (
            <motion.blockquote
              key={supporter.id}
              {...reveal(idx)}
              className="rule-gold pt-7 pb-10 last:pb-0"
            >
              <p className="font-heading text-2xl md:text-[1.75rem] leading-[1.35] text-ink">
                &ldquo;{t(supporter.quote)}&rdquo;
              </p>
              <footer className="mt-5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <cite className="not-italic text-sm font-semibold text-ink">
                  {supporter.name}
                </cite>
                <span className="label">{supporter.location}</span>
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </div>

      {/* ── Reach ── */}
      <motion.dl
        {...reveal()}
        className="grid grid-cols-2 md:grid-cols-4 mt-16 md:mt-24 border-t border-[color:var(--rule)]"
      >
        {impact.map((stat, i) => (
          <div
            key={stat.label}
            className={`py-7 md:py-9 border-b border-[color:var(--rule)] md:border-b-0 ${
              i % 2 === 1 ? 'pl-6 border-l' : ''
            } md:border-l md:first:border-l-0 md:px-8 md:first:pl-0 border-[color:var(--rule)]`}
          >
            <dd className="display-2 tabular">{stat.value}</dd>
            <dt className="label mt-3">{t(stat.label)}</dt>
          </div>
        ))}
      </motion.dl>

      <p className="lede mt-10">
        {t(
          'The Hyphen brand is worn by innovators in over 10 countries, symbolizing a commitment to technical rigor and African excellence.'
        )}
      </p>
    </Section>
  );
};

export default Community;
